import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Theme } from '@radix-ui/themes';
import '@radix-ui/themes/styles.css';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ErrorProvider } from './contexts/ErrorContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import AuthCallback from './components/AuthCallback';
import DummyPage from './components/DummyPage';
import BillingReport from './components/BillingReport';
import AdminScheduler from './components/AdminScheduler';
import InternalOnlyRoute from './components/auth/InternalOnlyRoute';
import ClinicAdminRoute from './components/auth/ClinicAdminRoute';
import AdminClinicsPage from './components/admin/AdminClinicsPage';
import AdminUsersPage from './components/admin/AdminUsersPage';
import AdminViewAsPage from './components/admin/AdminViewAsPage';
import AdminTellihealthPage from './components/admin/AdminTellihealthPage';
import AdminDevicesPage from './components/admin/AdminDevicesPage';
import AdminEligibilityPage from './components/admin/AdminEligibilityPage';
import AdminVoiceCallsPage from './components/admin/AdminVoiceCallsPage';
import AdminEhrSyncPage from './components/admin/AdminEhrSyncPage';
import AdminEhrUploadsPage from './components/admin/AdminEhrUploadsPage';
import AdminAdoptionTrackingPage from './components/admin/AdminAdoptionTrackingPage';
import ClinicUsersPage from './components/clinic/ClinicUsersPage';
import ClinicProfilePage from './components/clinic/ClinicProfilePage';
import ClinicDevicesPage from './components/clinic/ClinicDevicesPage';
import ClinicViewsPage from './components/clinic/ClinicViewsPage';
import ViewBuilder from './components/clinic/ViewBuilder';
import ReportsPage from './components/reports/ReportsPage';
import ReportRunPage from './components/reports/ReportRunPage';
import ScanDevicePage from './components/ScanDevicePage';
import VersionUpdateBanner from './components/VersionUpdateBanner';
import { FloatingChat, openAssistant } from './components/chat';
import { PatientTabsProvider } from './components/PatientTabs/PatientTabsContext';
import { Flex, Text } from '@radix-ui/themes';

// Loading component with Radix UI
const LoadingScreen: React.FC = () => (
  <Flex 
    direction="column"
    justify="center" 
    align="center" 
    className="min-h-screen"
    style={{
      background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
    }}
  >
    <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mb-4"></div>
    <Text size="5" style={{ color: 'white' }}>
      Connecting to Phanova CCM...
    </Text>
  </Flex>
);

// Protected Route component - shows login if not authenticated
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  // If not authenticated, show login page
  if (!user) {
    return <Login />;
  }
  
  return <>{children}</>;
};

const BillingAccessRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  // PHA-219: billing access for internal, clinic admins (org owners), billers,
  // and clinical medical directors. Plain clinical_care_manager is intentionally
  // excluded.
  const canAccessBilling =
    user?.is_internal ||
    user?.is_clinic_admin ||
    user?.roles?.includes('billing_operations_manager') ||
    user?.roles?.includes('clinical_medical_director');

  if (!canAccessBilling) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// PHA-1071: the agent panel is mounted once, above <Routes>, so a conversation
// survives navigation and is reachable from every authenticated screen —
// billing, admin, clinic settings — not just the dashboard, where it used to
// live (and where it unmounted, losing the thread, the moment you left).
// Rendered outside the route tree, so it needs its own auth gate: no chat on
// the login or Auth0 callback screens, and none before the user resolves.
const GlobalAgentChat: React.FC = () => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading || !user) return null;
  if (pathname === '/login' || pathname === '/callback') return null;
  return <FloatingChat />;
};

// PHA-1155: /assistant is a shareable link to the full-screen Ask Phanova
// workspace. The workspace itself is the global panel (so a conversation keeps
// living across navigation); this route just opens it full screen over the
// dashboard.
const OpenAssistantRoute: React.FC = () => {
  React.useEffect(() => {
    openAssistant('full');
  }, []);
  return <Navigate to="/" replace />;
};

// PHA-1168: a new report's dataset is fixed when the builder mounts, so keying on
// it gives a switch (Patients -> Billing month) a fresh builder, not mixed state.
const NewReportRoute: React.FC = () => {
  const [params] = useSearchParams();
  return <ViewBuilder key={params.get('dataset') || ''} kind="report" />;
};

// Authenticated app shell — wrapped in AuthProvider
const AuthenticatedApp: React.FC = () => {
  // PHA-1071: PatientTabsProvider sits above <Routes> so the Dashboard and the
  // agent panel share one tab context. The agent derives its scope (which
  // patient the clinician has open) from this context; with the provider still
  // inside Dashboard, the hoisted panel read null and every conversation
  // silently collapsed to clinic scope.
  const initialTabParam =
    new URLSearchParams(window.location.search).get('tab') === 'onboarding'
      ? 'onboarding'
      : 'patients';

  return (
    <AuthProvider>
      <OrganizationProvider>
        <PatientTabsProvider initialActiveTabId={initialTabParam}>
        <div className="min-h-screen bg-slate-1">
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/callback" element={<AuthCallback />} />
          <Route path="/dummy" element={
            <ProtectedRoute>
              <DummyPage />
            </ProtectedRoute>
          } />
          <Route path="/billing" element={
            <ProtectedRoute>
              <BillingAccessRoute>
                <BillingReport />
              </BillingAccessRoute>
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <InternalOnlyRoute>
                <Navigate to="/admin/clinics" replace />
              </InternalOnlyRoute>
            </ProtectedRoute>
          } />
          <Route path="/admin/clinics" element={
            <ProtectedRoute>
              <InternalOnlyRoute>
                <AdminClinicsPage />
              </InternalOnlyRoute>
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute>
              <InternalOnlyRoute>
                <AdminUsersPage />
              </InternalOnlyRoute>
            </ProtectedRoute>
          } />
          <Route path="/admin/view-as" element={
            <ProtectedRoute>
              <InternalOnlyRoute>
                <AdminViewAsPage />
              </InternalOnlyRoute>
            </ProtectedRoute>
          } />
          <Route path="/admin/scheduler" element={
            <ProtectedRoute>
              <InternalOnlyRoute>
                <AdminScheduler />
              </InternalOnlyRoute>
            </ProtectedRoute>
          } />
          <Route path="/admin/tellihealth" element={
            <ProtectedRoute>
              <InternalOnlyRoute>
                <AdminTellihealthPage />
              </InternalOnlyRoute>
            </ProtectedRoute>
          } />
          <Route path="/admin/devices" element={
            <ProtectedRoute>
              <InternalOnlyRoute>
                <AdminDevicesPage />
              </InternalOnlyRoute>
            </ProtectedRoute>
          } />
          <Route path="/admin/eligibility" element={
            <ProtectedRoute>
              <InternalOnlyRoute>
                <AdminEligibilityPage />
              </InternalOnlyRoute>
            </ProtectedRoute>
          } />
          <Route path="/admin/ehr-sync" element={
            <ProtectedRoute>
              <InternalOnlyRoute>
                <AdminEhrSyncPage />
              </InternalOnlyRoute>
            </ProtectedRoute>
          } />
          {/* PHA-1223. The API enforces internal-only independently (BFF requireInternal
              router + Flask is_internal). */}
          <Route path="/admin/ehr-uploads" element={
            <ProtectedRoute>
              <InternalOnlyRoute>
                <AdminEhrUploadsPage />
              </InternalOnlyRoute>
            </ProtectedRoute>
          } />
          {/* PHA-962. InternalOnlyRoute is the UI half; the API enforces
              requireInternal independently — the route guard alone would leave
              the endpoint reachable. */}
          <Route path="/admin/voice-calls" element={
            <ProtectedRoute>
              <InternalOnlyRoute>
                <AdminVoiceCallsPage />
              </InternalOnlyRoute>
            </ProtectedRoute>
          } />
          <Route path="/admin/adoption-tracking" element={
            <ProtectedRoute>
              <InternalOnlyRoute>
                <AdminAdoptionTrackingPage />
              </InternalOnlyRoute>
            </ProtectedRoute>
          } />
          <Route path="/clinic-settings" element={
            <ProtectedRoute>
              <ClinicAdminRoute>
                <Navigate to="/clinic-settings/users" replace />
              </ClinicAdminRoute>
            </ProtectedRoute>
          } />
          <Route path="/clinic-settings/users" element={
            <ProtectedRoute>
              <ClinicAdminRoute>
                <ClinicUsersPage />
              </ClinicAdminRoute>
            </ProtectedRoute>
          } />
          <Route path="/clinic-settings/profile" element={
            <ProtectedRoute>
              <ClinicAdminRoute>
                <ClinicProfilePage />
              </ClinicAdminRoute>
            </ProtectedRoute>
          } />
          <Route path="/clinic-settings/devices" element={
            <ProtectedRoute>
              <ClinicDevicesPage />
            </ProtectedRoute>
          } />
          {/* PHA-1168: gated inside the pages by /session/custom_views/enabled
              (flag + network-aware clinic-admin right), not ClinicAdminRoute, whose
              probe only admits a clinic the user owns directly. */}
          <Route path="/clinic-settings/views" element={
            <ProtectedRoute>
              <ClinicViewsPage />
            </ProtectedRoute>
          } />
          <Route path="/clinic-settings/views/:viewId" element={
            <ProtectedRoute>
              <ViewBuilder />
            </ProtectedRoute>
          } />
          {/* PHA-1168 Reports: gated inside the pages by /session/custom_views/enabled
              with kind=report (flag + design right; Phanova staff always, on any org). */}
          <Route path="/reports" element={
            <ProtectedRoute>
              <ReportsPage />
            </ProtectedRoute>
          } />
          {/* PHA-1243: a billing report designed on the Billing page lives there. */}
          <Route path="/billing/reports/new" element={
            <ProtectedRoute>
              <BillingAccessRoute>
                <ViewBuilder kind="report" home="billing" />
              </BillingAccessRoute>
            </ProtectedRoute>
          } />
          <Route path="/reports/new" element={
            <ProtectedRoute>
              <NewReportRoute />
            </ProtectedRoute>
          } />
          <Route path="/reports/:id/edit" element={
            <ProtectedRoute>
              <ViewBuilder kind="report" />
            </ProtectedRoute>
          } />
          <Route path="/reports/:id" element={
            <ProtectedRoute>
              <ReportRunPage />
            </ProtectedRoute>
          } />
          <Route
            path="/assistant"
            element={
              <ProtectedRoute>
                <OpenAssistantRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          <GlobalAgentChat />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#fff',
                color: '#333',
              },
            }}
          />
        </div>
        </PatientTabsProvider>
      </OrganizationProvider>
    </AuthProvider>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <Theme
      accentColor="blue"
      grayColor="slate"
      radius="medium"
      scaling="100%"
      appearance="light"
    >
      <ErrorProvider>
        {/* App-wide stale-frontend guard: prompts a reload when a newer build
            has been deployed than the one this tab booted with. */}
        <VersionUpdateBanner />
        <Router>
          <Routes>
            {/* Public route — no auth required (device qr code scanning url) */}
            <Route path="/scan/:token" element={<ScanDevicePage />} />
            {/* All other routes — wrapped in AuthProvider */}
            <Route path="/*" element={<AuthenticatedApp />} />
          </Routes>
        </Router>
      </ErrorProvider>
    </Theme>
  );
};

export default App;
